import { Button, FormControl, FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, useDisclosure, useToast, VStack } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { findId, getAuthCode, resetAuthCode } from "../../../api";
import { colors } from "../../../styles";
import { getApiErrorCode, getApiErrorMessage } from "../../../utils";

export default function Id() {

    /* toast & navigate */

    const toast = useToast();

    /* 모달 상태 관리 */

    const { isOpen, onOpen, onClose } = useDisclosure();

    /* emailFound */

    const [emailFound, setEmailFound] = useState<string | null>(null);

    /* useForm */

    interface IFindIdForm {
        name: string;
        phone: string;
        authCode: string;
    }

    const { register, handleSubmit, watch, formState: { errors } } = useForm<IFindIdForm>();

    /* 유효성 검사 */

    const isPhoneFilledByNumber = (value:string) => {
        if(value) {
            return value.trim() !== "" && !isNaN(Number(value));
        }
        return false;
    };

    /* 유효성 검사 */

    const isAuthCodeFilledByNumber = (value:string) => {
        if(value) {
            return value.trim() !== "" && !isNaN(Number(value));
        }
        return false;
    };

    /* 상태 관리 */

    const [errorStep, setErrorStep] = useState(0);
    const [getAuthCodeLoading, setGetAuthCodeLoading] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState("");

    /* 인증번호 전송 버튼 클릭 시 API 요청 */

    const onAuthCodeGet = async (phone:string) => {

        ////////////////////////////////////////////////////////////////////// 유효성 검사

        if(!isPhoneFilledByNumber(phone)) {
            setErrorStep(1.1);
            return;
        }
        
        //////////////////////////////////////////////////////////////////////

        setGetAuthCodeLoading(true);
        setApiErrorMessage("");

        ////////////////////////////////////////////////////////////////////// 인증번호 전송하기
        
        try {
            await getAuthCodeMutation.mutateAsync({ phone });
        } catch (error) {
            setApiErrorMessage(getAuthCodeErrorMessage(error));
        } finally {
            setGetAuthCodeLoading(false);
        }
        
        //////////////////////////////////////////////////////////////////////
        
    }

    /* timeLeft */

    const [timeLeft, setTimeLeft] = useState(180); // 3분 = 180초
    const [timerKey, setTimerKey] = useState(0);   // 타이머 재시작 트리거

    useEffect(() => {
        if(timerKey > 0) {

            setTimeLeft(180);

            const interval = setInterval(() => {
                setTimeLeft((prevTime) => {
                    if (prevTime <= 1) {
                        clearInterval(interval);
                        setErrorStep(1);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);

            return () => clearInterval(interval);
        }   
    }, [timerKey]);
    
    const formatTimeLeft = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    };

    /* 인증번호 재전송 버튼 클릭 시 API 요청 */

    const onAuthCodeReset = async (phone:string) => {

        ////////////////////////////////////////////////////////////////////// 유효성 검사

        if(!isPhoneFilledByNumber(phone)) {
            setErrorStep(1.1);
            return;
        }

        //////////////////////////////////////////////////////////////////////

        setGetAuthCodeLoading(true);
        setApiErrorMessage("");

        //////////////////////////////////////////////////////////////////////

        try {
            await resetAuthCodeMutation.mutateAsync({ phone });
            setErrorStep(0);
        } catch (error) {
            setApiErrorMessage(getAuthCodeErrorMessage(error));
        } finally {
            setGetAuthCodeLoading(false);
        }

        //////////////////////////////////////////////////////////////////////
    }

    /* on Find Id Submit */

    const onSubmit = async (data:IFindIdForm) => {

        ////////////////////////////////////////////////////////////////////// 유효성 검사

        if(!isAuthCodeFilledByNumber(data.authCode)) {
            setErrorStep(1.5);
            return;
        }

        ////////////////////////////////////////////////////////////////////// name, phone

        const { name, phone, authCode } = data;

        //////////////////////////////////////////////////////////////////////

        if(!name || !phone || !authCode) return;

        ////////////////////////////////////////////////////////////////////// 

        setApiErrorMessage("");

        try {
            await findIdMutation.mutateAsync({ name, phone, authCode });
        } catch (error) {
            setApiErrorMessage(getFindIdErrorMessage(error));
        }

    }

    const getAuthCodeErrorMessage = (error: unknown) => {
        // 서버 code: USER_NOT_FOUND | SMS_SEND_FAILED | RATE_LIMITED
        switch (getApiErrorCode(error)) {
            case "USER_NOT_FOUND":
                return "일치하는 회원 정보가 없습니다.";
            case "SMS_SEND_FAILED":
                return "인증번호 발송에 실패했습니다. 잠시 후 다시 시도해주세요.";
            case "RATE_LIMITED":
                return "인증번호 요청이 너무 많습니다. 잠시 후 다시 시도해주세요.";
            default:
                return getApiErrorMessage(error, "인증번호 처리 중 문제가 발생했습니다.");
        }
    };

    const getFindIdErrorMessage = (error: unknown) => {
        // 서버 code: USER_NOT_FOUND | USER_INFO_MISMATCH | AUTH_CODE_MISMATCH | AUTH_CODE_EXPIRED | AUTH_CODE_NOT_REQUESTED
        switch (getApiErrorCode(error)) {
            case "USER_NOT_FOUND":
                return "일치하는 회원 정보가 없습니다.";
            case "USER_INFO_MISMATCH":
                return "일치하는 정보가 없습니다.";
            case "AUTH_CODE_MISMATCH":
                return "인증번호가 일치하지 않습니다.";
            case "AUTH_CODE_EXPIRED":
                return "인증번호가 만료되었습니다. 다시 전송해주세요.";
            case "AUTH_CODE_NOT_REQUESTED":
                return "인증번호를 먼저 요청해주세요.";
            default:
                return getApiErrorMessage(error, "아이디 찾기 중 문제가 발생했습니다.");
        }
    };


    /* getAuthCode Mutation */

    const getAuthCodeMutation = useMutation({
        mutationFn: getAuthCode,
        onSuccess: (data) => {
            console.log("data", data);

            ////////////////////////////////////////////////////////////////////// 

            setTimerKey((key) => key + 1); // 전송 성공 → 타이머 리셋

        },
        onError: (error) => {
            console.log("error", error);

            //////////////////////////////////////////////////////////////////////
        }
    });

    /* resetAuthCode Mutation */

    const resetAuthCodeMutation = useMutation({
        mutationFn: resetAuthCode,
        onSuccess: (data) => {
            console.log("data", data);

            ////////////////////////////////////////////////////////////////////// toast

            toast({
                title: "인증번호가 재전송되었습니다.",
                description: "인증번호를 확인해주세요.",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top"
            });

            //////////////////////////////////////////////////////////////////////

            setTimerKey((key) => key + 1); // 재전송 성공 → 타이머 리셋
        },
        onError: (error) => {
            console.log("error", error);
        }
    });

    /* findId mutation */

    const findIdMutation = useMutation({
        mutationFn: findId,
        onSuccess: (data) => {
            console.log("data", data);

            const { user:{email} } = data;

            //////////////////////////////////////////////////////////////////////

            setEmailFound(email);

            //////////////////////////////////////////////////////////////////////

            onOpen();
        },
        onError: (error) => {
            console.log("error", error);
        }
    });


    /* 렌더링 */

    return (
        <>
            <VStack 
                padding={5}
                alignItems={"flex-start"}
            >
                <FormControl>
                    <FormLabel fontSize="sm" color={colors.blue}>
                        이름
                    </FormLabel>
                    <Input 
                        {...register("name", {required: {value:true, message:"이름을 입력해주세요."}})} 
                        type="text" 
                        isInvalid={Boolean(errors.name?.message)} 
                        placeholder={"이름 입력"} 
                        borderBottom={`1px solid ${colors.g4}`} 
                        borderTop="none" 
                        borderLeft="none" 
                        borderRight="none" 
                        borderRadius="0"
                        _focus={{
                            borderBottom: `1px solid ${colors.blue}`,
                            boxShadow: "none", 
                            outline: "none"
                            }}
                    />
                </FormControl>
                <FormControl marginTop={5}>
                    <FormLabel fontSize="sm" color={colors.blue}>
                        휴대폰 번호
                    </FormLabel>
                    <HStack width={"100%"} justifyContent={"space-between"} alignItems={"center"}>
                        <Input 
                            {...register("phone", {required: {value:true, message:"휴대폰 번호를 입력해주세요."}})} 
                            type="text" 
                            // readOnly={findIdPhoneNumber !== ""}
                            isInvalid={Boolean(errors.phone?.message)} 
                            placeholder={"휴대폰 번호 입력"}
                            borderBottom={`1px solid ${colors.g4}`} 
                            borderTop="none" 
                            borderLeft="none" 
                            borderRight="none" 
                            borderRadius="0" 
                            _focus={{
                                borderBottom: `1px solid ${colors.blue}`,
                                boxShadow: "none",
                                outline: "none"
                            }}
                        />
                        {timerKey === 0 ?
                            <Button 
                                height={"29px"}
                                color={colors.g1}
                                paddingX={5}
                                paddingY={1}
                                borderRadius={10}
                                onClick={() => onAuthCodeGet(watch("phone"))}
                                disabled={!isPhoneFilledByNumber(watch("phone"))} 
                                isLoading={getAuthCodeLoading}
                            >
                                <Text fontSize={14} fontWeight={500}>인증번호 전송</Text>
                            </Button>
                            :
                            <>
                                <Text fontSize={14} fontWeight={500} color={colors.red}>{formatTimeLeft(timeLeft)}</Text>
                                <Button 
                                    height={"29px"}
                                    color={colors.g1}
                                    paddingX={5}
                                    paddingY={1}
                                    borderRadius={10}
                                    onClick={() => onAuthCodeReset(watch("phone"))}
                                >
                                    <Text fontSize={14} fontWeight={500}>인증번호 재전송</Text>
                                </Button>
                            </>
                        }
                        
                    </HStack>
                </FormControl>
                {timerKey > 0 &&
                    <FormControl marginTop={5}>
                        <FormLabel fontSize="sm" color={colors.blue}>
                            인증번호
                        </FormLabel>
                        <Input 
                            {...register("authCode", {required: {value:true, message:"인증번호를 입력해주세요."}})} 
                            type="text" 
                            isInvalid={Boolean(errors.authCode?.message)} 
                            placeholder={"인증번호 입력"} 
                            borderBottom={`1px solid ${colors.g4}`} 
                            borderTop="none" 
                            borderLeft="none" 
                            borderRight="none" 
                            borderRadius="0"
                            _focus={{
                                borderBottom: `1px solid ${colors.blue}`,
                                boxShadow: "none", 
                                outline: "none"
                                }}
                        />
                    </FormControl>
                }
                {errors.name && <Text fontSize={14} fontWeight={500} color={colors.red}>{errors.name.message}</Text>}
                {errors.phone && <Text fontSize={14} fontWeight={500} color={colors.red}>{errors.phone.message}</Text>}
                {errors.authCode && <Text fontSize={14} fontWeight={500} color={colors.red}>{errors.authCode.message}</Text>}

                {errorStep === 1.1 && <Text fontSize={14} fontWeight={500} color={colors.red}>휴대폰 번호를 숫자로 입력주세요.</Text>}
                {errorStep === 1.2 && <Text fontSize={14} fontWeight={500} color={colors.red}>인증번호 전송 에러.</Text>}
                {errorStep === 1.3 && <Text fontSize={14} fontWeight={500} color={colors.red}>일치하는 정보가 없습니다.</Text>}
                {errorStep === 1.4 && <Text fontSize={14} fontWeight={500} color={colors.red}>인증번호를 재전송해주세요.</Text>}
                {errorStep === 1.5 && <Text fontSize={14} fontWeight={500} color={colors.red}>인증번호를 숫자로 입력해주세요.</Text>}
                {errorStep === 1.6 && <Text fontSize={14} fontWeight={500} color={colors.red}>인증번호가 일치하지 않습니다.</Text>}
                {apiErrorMessage && <Text fontSize={14} fontWeight={500} color={colors.red}>{apiErrorMessage}</Text>}

                <Button 
                    width={"100%"} 
                    height={"52px"}
                    backgroundColor={colors.blue} 
                    color={"white"} 
                    borderRadius={10}
                    marginTop={10}
                    onClick={handleSubmit(onSubmit)}
                    isLoading={findIdMutation.isPending}
                    disabled={!watch("name") || !watch("phone") || !isAuthCodeFilledByNumber(watch("authCode"))} 
                >
                    <Text fontSize={16} fontWeight={700}>다음</Text>
                </Button>
            </VStack>

            {/* 모달 */}

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader textAlign={"center"}>아이디 찾기 완료</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody textAlign={"center"} padding={5}>
                        <Text>{emailFound}</Text>
                        <Button 
                            onClick={() => {
                                navigator.clipboard.writeText(emailFound || "");
                                toast({
                                    title: "복사 완료",
                                    description: "아이디가 클립보드에 복사되었습니다.",
                                    status: "success",
                                    duration: 3000,
                                    isClosable: true,
                                    position: "top"
                                });
                            }}
                            mt={4}
                            size={"sm"}
                        >
                            복사하기
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}